import { useState, FormEvent } from 'react';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QuoteModal({ isOpen, onClose }: QuoteModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    projectSize: '',
    timeline: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('projectSize', formData.projectSize || 'Not specified');
      formBody.append('timeline', formData.timeline || 'Not specified');
      formBody.append('budget', formData.budget || 'Not specified');
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5ncnf3lpot2ks30e6ng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          projectSize: '',
          timeline: '',
          budget: '',
          message: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          type="button"
          className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <div className="p-6 sm:p-10 md:p-12">
          <div className="mb-8 sm:mb-10">
            <div className="text-[#2563EB] text-xs sm:text-sm font-bold uppercase tracking-wider mb-3">
              • FREE QUOTE
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get Your Free Quote
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Fill out the form below and we'll get back to you within 24 hours with a detailed quote for your project.
            </p>
          </div>

          <form id="quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <label htmlFor="quote-name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="quote-name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="quote-email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="quote-email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="quote-phone" className="block text-sm font-semibold text-gray-900 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="quote-phone"
                name="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm"
                placeholder="+1 (321) 000-0000"
              />
            </div>

            <div>
              <label htmlFor="quote-service" className="block text-sm font-semibold text-gray-900 mb-2">
                Service Needed *
              </label>
              <select
                id="quote-service"
                name="service"
                required
                value={formData.service}
                onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm cursor-pointer"
              >
                <option value="">Select a service</option>
                <option value="Bathroom Remodel">Bathroom Remodel</option>
                <option value="Kitchen Remodel">Kitchen Remodel</option>
                <option value="Interior Painting">Interior Painting</option>
                <option value="Exterior Painting">Exterior Painting</option>
                <option value="Flooring Installation">Flooring Installation</option>
                <option value="Drywall Work">Drywall Work</option>
                <option value="Multiple Services">Multiple Services</option>
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <label htmlFor="quote-projectSize" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Size
                </label>
                <select
                  id="quote-projectSize"
                  name="projectSize"
                  value={formData.projectSize}
                  onChange={(e) => setFormData({ ...formData, projectSize: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select size</option>
                  <option value="Small (1 room)">Small (1 room)</option>
                  <option value="Medium (2-3 rooms)">Medium (2-3 rooms)</option>
                  <option value="Large (4+ rooms)">Large (4+ rooms)</option>
                  <option value="Whole House">Whole House</option>
                </select>
              </div>

              <div>
                <label htmlFor="quote-timeline" className="block text-sm font-semibold text-gray-900 mb-2">
                  Timeline
                </label>
                <select
                  id="quote-timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select timeline</option>
                  <option value="ASAP">ASAP</option>
                  <option value="1-2 weeks">1-2 weeks</option>
                  <option value="1 month">1 month</option>
                  <option value="2-3 months">2-3 months</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="quote-budget" className="block text-sm font-semibold text-gray-900 mb-2">
                Budget Range
              </label>
              <select
                id="quote-budget"
                name="budget"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all text-sm cursor-pointer"
              >
                <option value="">Select budget</option>
                <option value="Under $5,000">Under $5,000</option>
                <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                <option value="$10,000 - $20,000">$10,000 - $20,000</option>
                <option value="$20,000 - $50,000">$20,000 - $50,000</option>
                <option value="Over $50,000">Over $50,000</option>
              </select>
            </div>

            <div>
              <label htmlFor="quote-message" className="block text-sm font-semibold text-gray-900 mb-2">
                Project Details *
              </label>
              <textarea
                id="quote-message"
                name="message"
                required
                rows={4}
                maxLength={500}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none transition-all resize-none text-sm"
                placeholder="Tell us about your project, what you'd like to achieve, any specific requirements..."
              />
              <div className="text-xs text-gray-500 mt-1">
                {formData.message.length}/500 characters
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                ✓ Thank you! We've received your request and will send you a quote within 24 hours.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                Something went wrong. Please try again or call us at +1 (321) 947-6724.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-8 py-4 rounded-lg text-base font-semibold transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Submitting...' : 'Get Your Free Quote'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}